import type { DeactivateProfilesResponse, ProfileSort } from '@shared/schemas';
import { all, call, getContext, put, select, takeLatest } from 'redux-saga/effects';

import type { FilterGroup } from '@/types/filters';

import type { ProfilesGateway, ProfilesPage } from '../infrastructure/profiles.gateway';
import {
  deactivateRequested,
  filtersChanged,
  pageChanged,
  pageSizeChanged,
  profilesRequested,
  sortChanged,
} from './profiles.actions';
import { selectFilters, selectPage, selectPageSize, selectSort } from './profiles.selectors';
import { profilesActions } from './profiles.slice';

const LOAD_ERROR = 'Failed to load profiles.';
const DEACTIVATE_ERROR = 'Failed to deactivate profiles.';

/** Best-effort human message for the failure banner / snackbar (no domain error union). */
function toMessage(e: unknown, fallback: string): string {
  if (e instanceof TypeError) {
    return 'Network error; please check your connection and retry.';
  }
  return e instanceof Error && e.message ? e.message : fallback;
}

/**
 * WORKER: fetch the current page. Reads `page` / `pageSize` / `filters` / `sort` back from
 * the store (the single source of truth for the fetch) and calls the gateway.
 * `takeLatest` cancels an in-flight fetch when a newer trigger arrives; the
 * abort-latest behaviour the old `useInfiniteApi` had.
 */
function* fetchCurrentPage() {
  const gateway: ProfilesGateway = yield getContext('profilesGateway');
  const page: number = yield select(selectPage);
  const pageSize: number = yield select(selectPageSize);
  const filters: FilterGroup | null = yield select(selectFilters);
  const sort: ProfileSort | null = yield select(selectSort);

  yield put(profilesActions.loadStarted());
  try {
    const result = (yield call(gateway.fetchProfiles, {
      filters,
      page,
      pageSize,
      sort,
    })) as ProfilesPage;
    yield put(profilesActions.loadSucceeded(result));
  } catch (e) {
    yield put(profilesActions.loadFailed(toMessage(e, LOAD_ERROR)));
  }
}

/** WORKER: apply new server filters (resets to page 0), then fetch. */
function* filtersChangedWorker(action: ReturnType<typeof filtersChanged>) {
  yield put(profilesActions.filtersSet(action.payload));
  yield* fetchCurrentPage();
}

/** WORKER: apply a new sort (resets to page 0), then fetch. */
function* sortChangedWorker(action: ReturnType<typeof sortChanged>) {
  yield put(profilesActions.sortSet(action.payload));
  yield* fetchCurrentPage();
}

/** WORKER: move to a page, then fetch it. */
function* pageChangedWorker(action: ReturnType<typeof pageChanged>) {
  yield put(profilesActions.pageSet(action.payload));
  yield* fetchCurrentPage();
}

/** WORKER: change the page size (resets to page 0), then fetch. */
function* pageSizeChangedWorker(action: ReturnType<typeof pageSizeChanged>) {
  yield put(profilesActions.pageSizeSet(action.payload));
  yield* fetchCurrentPage();
}

/**
 * WORKER: bulk-deactivate, then refresh the page. The success count feeds the
 * page's snackbar; the refetch replaces the deactivated rows.
 */
function* deactivateWorker(action: ReturnType<typeof deactivateRequested>) {
  const { ids, scope } = action.payload;
  if (ids.length === 0) return;
  const gateway: ProfilesGateway = yield getContext('profilesGateway');

  yield put(profilesActions.deactivateStarted());
  try {
    const res = (yield call(
      gateway.deactivateProfiles,
      ids,
      scope,
    )) as DeactivateProfilesResponse;
    yield put(profilesActions.deactivateSucceeded(res.deactivated_count));
    yield* fetchCurrentPage();
  } catch (e) {
    yield put(profilesActions.deactivateFailed(toMessage(e, DEACTIVATE_ERROR)));
  }
}

/** WATCHERS. */
export function* profilesSaga() {
  yield all([
    takeLatest(profilesRequested.type, fetchCurrentPage),
    takeLatest(filtersChanged.type, filtersChangedWorker),
    takeLatest(sortChanged.type, sortChangedWorker),
    takeLatest(pageChanged.type, pageChangedWorker),
    takeLatest(pageSizeChanged.type, pageSizeChangedWorker),
    takeLatest(deactivateRequested.type, deactivateWorker),
  ]);
}
